import type { Observable } from 'rxjs';
import type { CollectionQueryStateStatuses } from '@common/adapters/collection-query';
import type { ChatBotEntity } from '@common/types/chat-bot.type';
import type { ChatBotsCollectionState } from './chat-bots.reducer';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { mergeMap, of } from 'rxjs';

import { ESocialNetworks } from '@common/enums';

import ChatBotsActions from './chat-bots.actions';
import * as ChatBotsSelectors from './chat-bots.selectors';
import { map } from 'rxjs/operators';

@Injectable()
export class ChatBotsCollectionFacade {
    private readonly store = inject<Store<ChatBotsCollectionState>>(Store);

    private readonly chatBotsAvailableMessengers = [
        ESocialNetworks.telegram,
        ESocialNetworks.messenger,
        ESocialNetworks.instagram,
        ESocialNetworks.viber,
        ESocialNetworks.whatsapp,
        ESocialNetworks.tiktok,
    ];

    public loadChatBotsCollection(messenger: string): void {
        this.store.dispatch(ChatBotsActions.loadCollectionRequest({ messenger }));
    }

    public chatBots$(messenger: string): Observable<ChatBotEntity[]> {
        if (this.chatBotsAvailableMessengers.indexOf(messenger as ESocialNetworks) === -1) {
            return of([]);
        }

        return this.store.select(ChatBotsSelectors.selectChatBots(messenger)).pipe(
            mergeMap((chatBots: ChatBotEntity[]) => {
                if (!chatBots) {
                    this.loadChatBotsCollection(messenger);
                    return of(undefined);
                }

                return of(chatBots ?? undefined);
            })
        );
    }

    public chatBotVariables$(
        messenger: string,
        id: string
    ): Observable<
        {
            created_at: string;
            bot_id: string;
            _id: string;
            name: string;
            updated_at: string;
            value: number;
            value_type: number;
        }[]
    > {
        if (this.chatBotsAvailableMessengers.indexOf(messenger as ESocialNetworks) === -1) {
            return of(undefined);
        }

        return this.store.select(ChatBotsSelectors.selectChatBots(messenger)).pipe(
            map((chatBots: ChatBotEntity[]) => {
                const bot = chatBots.find((b) => b?.id?.toString() === id?.toString());
                return bot?.variables ?? [];
            })
        );
    }

    public chatBotsStatuses$(messenger: string): Observable<CollectionQueryStateStatuses> {
        if (!messenger) {
            return of({} as any);
        }

        return this.store.select(ChatBotsSelectors.selectChatBotsStatuses(messenger));
    }
}
